/**
 * Shared template data used by both the Explore page and world creation dialog.
 * Each template maps to an explore game example with metadata for auto-generation.
 */

export interface WorldTemplate {
  gameKey: string;
  category: string;
  tagKeys: string[];
  /** Mechanics to enable (maps to mechanic registry keys) */
  mechanics: string[];
}

/**
 * Maps tagKeys to mechanic registry IDs for auto-enabling.
 * Only essential mechanics — keep it minimal per user request.
 *
 * NOTE: values MUST be the underscore mechanic IDs (matching ACTIVATABLE_MECHANIC_IDS
 * in validate-active-mechanics.ts and the registry's SUB_GROUP_MAP) — NOT the
 * hyphenated module filenames (e.g. id `magic_system` lives in `magic-system.ts`).
 * A test in src/test guards this invariant.
 */
const TAG_TO_MECHANIC: Record<string, string> = {
  territory_control: "territory",
  organization: "organization",
  heat_system: "resources",
  combat: "combat",
  magic_system: "magic_system",
  quest_system: "quest_journal",
  quest_journal: "quest_journal",
  survival: "survival",
  economy: "economy",
  resources: "resources",
  reputation: "reputation",
  morality: "morality",
  region_movement: "region_movement",
  influence: "influence",
  dialogue_system: "dialogue_system",
  skill_checks: "skill_checks",
  character_progression: "character_progression",
  inventory: "inventory",
  inner_voice: "inner_voice",
  lore_discovery: "lore_discovery",
};

/** All template game keys (same order as Explore page) */
export const TEMPLATE_KEYS = [
  "cartel", "shadow", "galactic", "godfather", "feudal", "rebel",
  "dungeon", "potion", "tavern", "station", "crime", "gladiator", "mercenary",
  "hogwarts", "westeros", "middleearth", "starfleet", "jedi", "peaky", "naruto",
  "darkest", "wasteland", "court", "pirate", "vampire",
  "courtroom", "island", "heist", "political",
  "sky", "underdark", "clockwork", "arcane", "sand", "frozen",
  "verdant", "deep", "neon", "plague", "iron", "silk",
  "hw", "hh", "hu", "hi", "hg", "ha", "hv", "has",
] as const;

const TEMPLATE_META: Record<string, { category: string; tagKeys: string[] }> = {
  cartel:      { category: "rts",    tagKeys: ["territory_control", "organization", "heat_system"] },
  shadow:      { category: "rts",    tagKeys: ["organization", "skill_checks"] },
  galactic:    { category: "rts",    tagKeys: ["territory_control", "resources"] },
  godfather:   { category: "rts",    tagKeys: ["organization", "heat_system", "influence"] },
  feudal:      { category: "rts",    tagKeys: ["territory_control", "organization"] },
  rebel:       { category: "rts",    tagKeys: ["organization", "resources"] },
  dungeon:     { category: "tycoon", tagKeys: ["economy", "resources"] },
  potion:      { category: "tycoon", tagKeys: ["economy", "resources"] },
  tavern:      { category: "tycoon", tagKeys: ["economy", "reputation"] },
  station:     { category: "tycoon", tagKeys: ["resources", "economy", "survival"] },
  crime:       { category: "tycoon", tagKeys: ["organization", "economy"] },
  gladiator:   { category: "tycoon", tagKeys: ["economy", "reputation", "combat"] },
  mercenary:   { category: "tycoon", tagKeys: ["organization", "resources", "quest_system"] },
  hogwarts:    { category: "fanfic", tagKeys: ["magic_system", "quest_journal", "reputation"] },
  westeros:    { category: "fanfic", tagKeys: ["influence", "territory_control", "morality"] },
  middleearth: { category: "fanfic", tagKeys: ["region_movement", "combat", "lore_discovery"] },
  starfleet:   { category: "fanfic", tagKeys: ["skill_checks", "reputation", "character_progression"] },
  jedi:        { category: "fanfic", tagKeys: ["morality", "survival", "inner_voice"] },
  peaky:       { category: "fanfic", tagKeys: ["organization", "territory_control"] },
  naruto:      { category: "fanfic", tagKeys: ["organization", "skill_checks"] },
  darkest:     { category: "rpg",    tagKeys: ["survival", "combat", "inventory"] },
  wasteland:   { category: "rpg",    tagKeys: ["survival", "region_movement", "economy"] },
  court:       { category: "rpg",    tagKeys: ["dialogue_system", "reputation", "morality"] },
  pirate:      { category: "rpg",    tagKeys: ["organization", "economy", "combat"] },
  vampire:     { category: "rpg",    tagKeys: ["morality", "influence", "territory_control"] },
  courtroom:   { category: "other",  tagKeys: ["dialogue_system", "skill_checks", "morality"] },
  island:      { category: "other",  tagKeys: ["survival", "resources", "morality"] },
  heist:       { category: "other",  tagKeys: ["skill_checks", "organization"] },
  political:   { category: "other",  tagKeys: ["influence", "reputation", "morality"] },
  sky:         { category: "rts",    tagKeys: ["territory_control", "resources", "combat"] },
  underdark:   { category: "rpg",    tagKeys: ["survival", "resources", "reputation"] },
  clockwork:   { category: "rpg",    tagKeys: ["lore_discovery", "dialogue_system", "inner_voice"] },
  arcane:      { category: "rpg",    tagKeys: ["magic_system", "skill_checks", "morality"] },
  sand:        { category: "rpg",    tagKeys: ["region_movement", "lore_discovery", "survival"] },
  frozen:      { category: "rpg",    tagKeys: ["survival", "territory_control", "combat"] },
  verdant:     { category: "tycoon", tagKeys: ["organization", "resources", "reputation"] },
  deep:        { category: "tycoon", tagKeys: ["resources", "economy", "survival"] },
  neon:        { category: "other",  tagKeys: ["combat", "economy", "morality"] },
  plague:      { category: "other",  tagKeys: ["survival", "morality", "resources"] },
  iron:        { category: "other",  tagKeys: ["resources", "region_movement", "morality"] },
  silk:        { category: "tycoon", tagKeys: ["economy", "region_movement", "dialogue_system"] },
  hw:          { category: "horror", tagKeys: ["survival", "resources"] },
  hh:          { category: "horror", tagKeys: ["lore_discovery", "survival"] },
  hu:          { category: "horror", tagKeys: ["region_movement", "dialogue_system"] },
  hi:          { category: "horror", tagKeys: ["survival", "lore_discovery"] },
  hg:          { category: "horror", tagKeys: ["survival", "lore_discovery"] },
  ha:          { category: "horror", tagKeys: ["survival", "resources", "combat"] },
  hv:          { category: "horror", tagKeys: ["survival", "combat", "organization"] },
  has:         { category: "horror", tagKeys: ["lore_discovery", "survival"] },
};

/** Get a fully resolved template with mechanics */
export function getTemplate(gameKey: string): WorldTemplate | null {
  const meta = TEMPLATE_META[gameKey];
  if (!meta) return null;
  const mechanicSet = new Set<string>();
  for (const tag of meta.tagKeys) {
    const mech = TAG_TO_MECHANIC[tag];
    if (mech) mechanicSet.add(mech);
  }
  return {
    gameKey,
    category: meta.category,
    tagKeys: meta.tagKeys,
    mechanics: [...mechanicSet],
  };
}

/** Get all templates as array */
export function getAllTemplates(): WorldTemplate[] {
  return TEMPLATE_KEYS.map(k => getTemplate(k)!).filter(Boolean);
}
